import { observer } from 'src/utils/mobx-react';
import * as React from 'react';
import {
    PopupContent,
    PopupBody,
    PopupTitle,
    PopupClose,
    PopupOverlayDefault,
    CenterIconWrapper,
    ColorOverlay,
} from './Popup/Popup.style';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

function ignoreClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    e.stopPropagation();
}

export const ICON_POSITION = {
    TOP: 'top',
    LEFT: 'left',
} as const;

type IconOptionsType = {
    position: (typeof ICON_POSITION)[keyof typeof ICON_POSITION];
};

type PopupOptionsType = {
    iconOptions: IconOptionsType;
};

interface PropsType {
    className?: string;
    onClose?: (e: React.SyntheticEvent) => void;
    icon?: React.ReactNode;
    title?: string | JSX.Element;
    closeButton?: boolean;
    maxWidth?: string;
    priority?: number;
    overlayClose?: boolean;
    children: React.ReactNode;
    dataTest?: string;
    options?: PopupOptionsType;
    colorOverlay?: boolean;
}

export const Popup = observer('Popup', (props: PropsType): React.ReactElement => {
    const {
        icon,
        title,
        children,
        closeButton = true,
        priority = 0,
        overlayClose = false,
        className,
        maxWidth,
        dataTest,
        options,
        colorOverlay = false,
    } = props;

    const common = useCommon();
    const configState = ConfigComponents.get(common).config;

    const close = (e: React.SyntheticEvent): void => {
        e.preventDefault();
        e.stopPropagation();

        if (props.onClose !== undefined) {
            props.onClose(e);
        }
    };

    const zIndex = 10000 + priority;

    let onOverlayClick;
    if (overlayClose) {
        onOverlayClick = close;
    }

    let titleEl;
    if (title !== undefined) {
        let closeButtonEl;
        if (closeButton) {
            closeButtonEl = (
                <PopupClose
                    onClick={close}
                    className='popup-close'
                >
                    &times;
                </PopupClose>
            );
        }

        titleEl = (
            <PopupTitle>
                {options === undefined || options.iconOptions.position === ICON_POSITION.LEFT ? icon : null}
                {title}
                {closeButtonEl}
            </PopupTitle>
        );
    }

    return (
        <ColorOverlay colorOverlay={colorOverlay}>
            <PopupOverlayDefault
                active={true}
                className={className}
                style={{ zIndex }}
                onClick={onOverlayClick}
                data-test='popup-overlay'
            >
                <PopupContent
                    onClick={ignoreClick}
                    maxWidth={maxWidth}
                    data-test={dataTest}
                    variant={configState.componentVariants.modalVariant}
                >
                    {options?.iconOptions.position === ICON_POSITION.TOP ? (
                        <CenterIconWrapper>{icon}</CenterIconWrapper>
                    ) : null}
                    {titleEl}
                    <PopupBody>{children}</PopupBody>
                </PopupContent>
            </PopupOverlayDefault>
        </ColorOverlay>
    );
});

export { PopupOverlayDefault };
